import '@fontsource/raleway';
import '@fontsource/heebo';
import '@fontsource/heebo/200.css';
import '@fontsource/heebo/300.css';
import '@fontsource/heebo/500.css';
import '@fontsource/heebo/600.css';
import '@fontsource/heebo/700.css';
import '@fontsource-variable/outfit';
import 'bootstrap/dist/css/bootstrap.min.css';
const { CustomProvider } = require('./custom-context');
const React = require('react');

export function onClientEntry() {
	if (typeof window === 'undefined') return;

	// 1) Create a safe stub so existing window.analytics.* calls never crash
	if (!window.analytics) {
		const noop = () => {};
		window.analytics = {
			page: noop,
			track: noop,
			identify: noop,
			group: noop,
			alias: noop,
			reset: noop,
			load: noop,
		};
	}

	// 2) Only actually LOAD Segment on the real production hostname
	const PROD_HOST = 'www.wilaustralia.edu.au';
	const currentHost = window.location.hostname;
	const isProd = currentHost === PROD_HOST;

	if (!isProd) {
		// Previews, branch deploys, localhost, etc → do NOT load Segment
		console.log('[Segment] Not loading Segment on host:', currentHost);
		return;
	}

	// On the real production host → now call analytics.load(writeKey)
	const writeKey = process.env.GATSBY_SEGMENT_KEY;

	if (!writeKey) {
		console.warn(
			'[Segment] GATSBY_SEGMENT_KEY is missing on production host:',
			currentHost
		);
		return;
	}

	if (typeof window.analytics.load === 'function') {
		window.analytics.load(writeKey);
		console.log('[Segment] Loaded Segment on production host:', currentHost);
	} else {
		console.log(
			'[Segment] analytics.load is not available yet when onClientEntry ran.'
		);
	}
}

export function wrapPageElement({ element, props }) {
	return <CustomProvider {...props}>{element}</CustomProvider>;
}

export function shouldUpdateScroll({ routerProps, getSavedScrollPosition }) {
	// Always scroll to top on route changes, except for hash links
	if (routerProps.location.hash) {
		return false; // Let browser handle hash scrolling
	}
	return [0, 0]; // Always scroll to top
}

export function onRouteUpdate({ location, prevLocation }) {
	// Handle hash navigation
	if (location?.hash) {
		// Wait for the page to load and then scroll to the hash element
		setTimeout(() => {
			const hash = location.hash;
			let element = document.querySelector(hash);

			// If element not found, try URL decoding the hash
			if (!element) {
				const decodedHash = decodeURIComponent(hash);
				element = document.querySelector(decodedHash);
			}

			// If still not found, try to find by text content (for cases where ID might be different)
			if (!element) {
				const hashText = hash
					.replace('#', '')
					.replace(/-/g, ' ')
					.replace(/&/g, '&');
				const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
				for (let heading of headings) {
					if (
						heading.textContent.toLowerCase().includes(hashText.toLowerCase())
					) {
						element = heading;
						break;
					}
				}
			}

			if (element) {
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			}
		}, 100);
		return;
	}

	// Handle regular page navigation (scroll to top)
	if (location?.pathname !== prevLocation?.pathname) {
		// Detect Firefox browser
		const isFirefox =
			typeof window !== 'undefined' &&
			window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

		// For Firefox, use a more aggressive approach to ensure scroll to top
		if (isFirefox) {
			// Immediate scroll to top
			window?.scrollTo(0, 0);

			// Force scroll on all possible scrollable elements
			if (document.documentElement) {
				document.documentElement.scrollTop = 0;
			}
			if (document.body) {
				document.body.scrollTop = 0;
			}

			// Use multiple timeouts to ensure Firefox scrolls to top
			setTimeout(() => {
				window?.scrollTo(0, 0);
				if (document.documentElement) {
					document.documentElement.scrollTop = 0;
				}
				if (document.body) {
					document.body.scrollTop = 0;
				}
			}, 0);

			setTimeout(() => {
				window?.scrollTo(0, 0);
				if (document.documentElement) {
					document.documentElement.scrollTop = 0;
				}
				if (document.body) {
					document.body.scrollTop = 0;
				}
			}, 10);

			setTimeout(() => {
				window?.scrollTo(0, 0);
				if (document.documentElement) {
					document.documentElement.scrollTop = 0;
				}
				if (document.body) {
					document.body.scrollTop = 0;
				}
			}, 50);
		} else {
			// For other browsers, use the existing approach
			window?.scrollTo(0, 0);

			// Also try scrolling the document element and body for better compatibility
			if (document.documentElement) {
				document.documentElement.scrollTop = 0;
			}
			if (document.body) {
				document.body.scrollTop = 0;
			}

			// Use requestAnimationFrame to ensure scroll happens after DOM updates
			requestAnimationFrame(() => {
				window?.scrollTo(0, 0);
			});
		}
	}
}
